import React, { Fragment, useMemo } from 'react';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import useClipboard from 'react-use-clipboard';
import moment from 'moment';

import Button from '../Button';
import usePrepaidItemsPurchase from './hooks/usePrepaidItemsPurchase';
import IdentitySelector from './components/identitySelector';
import ScrollableDiv from '../ScrollableDiv';
import { getCartTotalItems } from '../../../apps/web-gallery/utilities/web-gallery-cart-utils';
import {
  cartItemUpdate,
  toggleAlbumCustomPrintCartItem,
  toggleAlbumFolderSubscriptionCartItem,
  toggleAlbumPrintPackageCartItem,
  toggleDigitalBundleCartItem,
  toggleDigitalDownloadCartItem,
  togglePhotoPricingPrintProductCartItem,
} from '../../../services/checkout/redux/checkoutActions';
import Alert from '../Alert';
import { DISCOUNT_MODE } from '../../../util/promo-codes';
import { formatPriceForDisplay } from '../../../util/format-money';
import CheckmarkIcon from '../../icons/CheckmarkIcon';
import PrintPackagesItemsSelector from './components/PrintPackagesItemsSelector';
import PhotoPricingItemsSelector from './components/PhotoPricingItemsSelector';
import DownloadsSelector from './components/EntitySubscriptionSelector';
import AlbumCustomPrintItemsSelector from './components/AlbumCustomPrintItemsSelector';
import { getItemsCountByIdentity } from './helpers';
import ContextualString from '../../contextual/ContextualString';
import useIdentityJerseyColors from '../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';

const propTypes = {
  isOnboarding: PropTypes.bool,
  albumFolderId: PropTypes.string.isRequired,
  cart: PropTypes.array.isRequired,
  activeIdentity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumPrintPackageToggle: PropTypes.func,
  onAlbumFolderSubscriptionToggle: PropTypes.func,
  onDigitalDownloadToggle: PropTypes.func,
  onPhotoPricingPrintProductToggle: PropTypes.func,
  onAlbumCustomPrintToggle: PropTypes.func,
  onDigitalBundleToggle: PropTypes.func,
  onCartItemUpdate: PropTypes.func,
  onCheckout: PropTypes.func.isRequired,
  onAddFace: PropTypes.func,
  onSkip: PropTypes.func,
};

const PrepaidItemsPurchase = ({
  isOnboarding,
  albumFolderId,
  cart,
  activeIdentity,
  onAlbumPrintPackageToggle,
  onAlbumFolderSubscriptionToggle,
  onDigitalDownloadToggle,
  onPhotoPricingPrintProductToggle,
  onAlbumCustomPrintToggle,
  onDigitalBundleToggle,
  onCartItemUpdate,
  onCheckout,
  onAddFace,
  onSkip,
}) => {
  const { jerseyColors } = useIdentityJerseyColors({
    identityId: activeIdentity?.id,
    albumFolderId,
  });

  const {
    identities,
    selectedIdentities,
    loadingIdentities,
    albumFolder,
    printPackages,
    photoPricing,
    albumCustomPrints,
    digitalBundles,
    activePromoCode,
    hasNoFaceRec,
  } = usePrepaidItemsPurchase({ albumFolderId, isOnboarding, jerseyColors });
  const [promoCodeCopied, setPromoCodeCopied] = useClipboard(
    activePromoCode?.code,
  );

  const cartTotalItems = useMemo(() => getCartTotalItems(cart), [cart]);
  const canCheckout = useMemo(() => {
    if (isOnboarding) {
      return !(identities ?? []).some(
        identity => getItemsCountByIdentity(cart, identity) === 0,
      );
    }

    return !loadingIdentities && cartTotalItems > 0;
  }, [loadingIdentities, cartTotalItems, isOnboarding]);

  // @TODO Re-enable this once we fully support digital downloads on GS
  const isGreenScreenEnabled = albumFolder?.green_screen_enabled;

  const scrollableContainerClasses = classNames({
    'prepaid-items-scroller': true,
    'with-promo-code-alert': !!activePromoCode,
  });

  return (
    <Fragment>
      {activePromoCode && (
        <Alert type="info" className="prepaid-items-alert">
          {activePromoCode.expires_at && (
            <p className="text-sm text-primary text-uppercase mb-0 text-extra-bold">
              LIMITED TIME OFFER!
            </p>
          )}
          Use promo code &quot;
          <span className="text-primary text-extra-bold">
            {activePromoCode.code}
          </span>
          &quot;{' '}
          {activePromoCode.expires_at ? (
            <>
              and purchase your prints prior to{' '}
              <span className="text-primary text-extra-bold">
                {moment.utc(activePromoCode.expires_at).format('MMMM D, yyyy')}
              </span>{' '}
            </>
          ) : (
            ''
          )}
          to save{' '}
          <span className="text-primary text-extra-bold">
            {activePromoCode.discount.mode === DISCOUNT_MODE.PERCENTAGE
              ? `${activePromoCode.discount.value}%`
              : `$${formatPriceForDisplay(
                  activePromoCode.discount.value,
                  true,
                )}`}
          </span>
          !
          <Button
            pill
            btnOutline
            btnSize="sm"
            className="prepaid-items-promo-code-copy"
            onClick={() => setPromoCodeCopied(true)}
          >
            {promoCodeCopied ? (
              <Fragment>
                <div>
                  <CheckmarkIcon size={12} color="white" />
                </div>{' '}
                Copied to clipboard
              </Fragment>
            ) : (
              'Copy promo code'
            )}
          </Button>
        </Alert>
      )}
      <div className="h-100 w-100 d-flex flex-column">
        {!hasNoFaceRec && (
          <Fragment>
            <h4 className="w-100 text-left text-md-center">
              Select your items below for:
            </h4>
            <IdentitySelector
              identities={identities}
              onAddFace={onAddFace}
              isOnboarding={isOnboarding}
            />
          </Fragment>
        )}
        {(activeIdentity || hasNoFaceRec) && (
          <ScrollableDiv className={scrollableContainerClasses}>
            <PrintPackagesItemsSelector
              cart={cart}
              activeIdentity={activeIdentity}
              albumFolder={albumFolder}
              printPackages={printPackages}
              onCartItemUpdate={onCartItemUpdate}
              onAlbumPrintPackageToggle={onAlbumPrintPackageToggle}
            />
            <PhotoPricingItemsSelector
              cart={cart}
              activeIdentity={activeIdentity}
              selectedIdentity={identities.find(identity => {
                if (isOnboarding) {
                  return identity.id === activeIdentity?.id;
                }
                // If we are not within onboarding, we're using a membership's identity.
                return identity.identity.id === activeIdentity.id;
              })}
              albumFolder={albumFolder}
              photoPricing={photoPricing}
              onPhotoPricingPrintProductToggle={
                onPhotoPricingPrintProductToggle
              }
            />
            <AlbumCustomPrintItemsSelector
              cart={cart}
              activeIdentity={activeIdentity}
              onCartItemUpdate={onCartItemUpdate}
              albumFolder={albumFolder}
              albumCustomPrints={albumCustomPrints}
              selectedIdentities={selectedIdentities}
              onAlbumCustomPrintToggle={onAlbumCustomPrintToggle}
            />
            {!isGreenScreenEnabled && (
              <DownloadsSelector
                cart={cart}
                activeIdentity={activeIdentity}
                selectedIdentity={identities.find(identity => {
                  if (isOnboarding) {
                    return identity.id === activeIdentity?.id;
                  }
                  // If we are not within onboarding, we're using a membership's identity.
                  return identity.identity.id === activeIdentity.id;
                })}
                albumFolder={albumFolder}
                photoPricing={photoPricing}
                digitalBundles={digitalBundles}
                selectedIdentities={selectedIdentities}
                onAlbumFolderSubscriptionToggle={
                  onAlbumFolderSubscriptionToggle
                }
                onDigitalBundleToggle={onDigitalBundleToggle}
                onCartItemUpdate={onCartItemUpdate}
                onDigitalDownloadToggle={onDigitalDownloadToggle}
              />
            )}
          </ScrollableDiv>
        )}
        {(cart.length > 0 || onSkip) && (
          <div className="prepaid-items-checkout-btn">
            {cart.length > 0 && (
              <Button
                disabled={!canCheckout}
                btnBlock
                btnStyle="success"
                className={onSkip ? 'mb-2' : ''}
                onClick={() => {
                  if (!canCheckout) {
                    return;
                  }

                  onCheckout();
                }}
              >
                {!canCheckout ? (
                  <span>
                    {isOnboarding ? (
                      <>
                        Select at least 1 item per{' '}
                        <ContextualString
                          id="identityReference"
                          defaultMessage="person"
                          type={albumFolder?.album_type}
                          subtype={albumFolder?.album_subtype}
                        />
                      </>
                    ) : (
                      'Loading...'
                    )}
                  </span>
                ) : (
                  <Fragment>
                    {isOnboarding ? 'Continue' : 'Checkout'}{' '}
                    {cartTotalItems > 0 && (
                      <span>({pluralize('item', cartTotalItems, true)})</span>
                    )}
                  </Fragment>
                )}
              </Button>
            )}
            {onSkip && (
              <Button
                btnStyle="link"
                className="text-center"
                btnBlock
                onClick={onSkip}
              >
                Preview for free & Purchase later
              </Button>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
};

PrepaidItemsPurchase.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: state.checkout.cart,
  activeIdentity: state.checkout.activeIdentity,
});

const mapDispatchToProps = {
  onAlbumPrintPackageToggle: toggleAlbumPrintPackageCartItem,
  onAlbumFolderSubscriptionToggle: toggleAlbumFolderSubscriptionCartItem,
  onDigitalDownloadToggle: toggleDigitalDownloadCartItem,
  onPhotoPricingPrintProductToggle: togglePhotoPricingPrintProductCartItem,
  onAlbumCustomPrintToggle: toggleAlbumCustomPrintCartItem,
  onDigitalBundleToggle: toggleDigitalBundleCartItem,
  onCartItemUpdate: cartItemUpdate,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PrepaidItemsPurchase,
);
